import { useMemo } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  FolderOpen,
  Plus,
  Palette,
  Activity,
  ArrowRight,
  AlertTriangle,
  Sparkles,
} from 'lucide-react';
import { brandsApi } from '@/features/brands/api';
import {
  xamplinApi,
  deriveEventStatus,
  entityIdOf,
  PURPOSE_BY_ID,
  type XpProject,
  type XpEventAsset,
  type XpStructureEvent,
} from '@/lib/api/xamplin';
import { queryKeys } from '@/lib/api/queryKeys';
import {
  useSelectedBrandId,
  useBrandContextStore,
} from '@/lib/branding/brandContextStore';
import { useCurrentCompany } from '@/lib/branding/useCurrentBrand';
import { useAuth } from '@/lib/auth/useAuth';
import { cn } from '@/lib/cn';
import { CountUp } from '@/components/ui/CountUp';

/**
 * Xamplin Home — multi-brand command center (build-v2 F2 + G1).
 *
 * Faithful to the prototype `home.jsx`: ATLEDI greeting + live nudge + action
 * cards + recents, a workspace summary (live/gaps), the photo strip, brand
 * switcher, brand hero, the PTM pipeline and the Today / This week / Needs-you
 * feeds — all derived from REAL data (brands, projects, project structures).
 */

/** A flattened, status-derived event used across the Home surfaces. */
interface HomeEvent {
  id: number;
  name: string;
  group: string;
  projectId: number;
  status: 'plan' | 'live' | 'completed';
  start: number;
  stations: number;
  reporters: number;
  hasGaps: boolean;
}

const DAY = 86_400_000;

export function HomePage() {
  const { user } = useAuth();
  const { company } = useCurrentCompany();
  const selectedBrandId = useSelectedBrandId();
  const setSelectedBrandId = useBrandContextStore((s) => s.setSelectedBrandId);

  const brandsQ = useQuery({
    queryKey: queryKeys.brands.all,
    queryFn: () => brandsApi.list(),
  });
  const projectsQ = useQuery({
    queryKey: queryKeys.xamplin.projects(),
    queryFn: () => xamplinApi.projects.list(),
  });
  const assetsQ = useQuery({
    queryKey: queryKeys.xamplin.assets,
    queryFn: () => xamplinApi.assets.list(300),
  });
  const fieldPhotos = (assetsQ.data?.items ?? []).filter((a) => a.image).slice(0, 12);

  const brands = (brandsQ.data?.items ?? []).filter((b) => b.companyType !== 'Agency');
  const projects = projectsQ.data?.items ?? [];

  const activeBrandId = selectedBrandId ?? brands[0]?.id ?? null;
  const activeBrand = brands.find((b) => b.id === activeBrandId) ?? null;
  const brandProjects = useMemo(
    () => projects.filter((p) => p.companyId === activeBrandId),
    [projects, activeBrandId],
  );
  const totalSamples = brandProjects.reduce((s, p) => s + (p.samples ?? 0), 0);

  // Structures of the active brand's projects. Same query keys as PtmPipeline,
  // so React Query dedupes — one fetch feeds the pipeline, feeds and nudge.
  const structureQs = useQueries({
    queries: brandProjects.map((p) => ({
      queryKey: queryKeys.xamplin.projectStructure(p.id),
      queryFn: () => xamplinApi.projects.structure(p.id),
    })),
  });
  const events = useMemo<HomeEvent[]>(() => {
    const out: HomeEvent[] = [];
    structureQs.forEach((q, i) => {
      const proj = brandProjects[i];
      if (!proj) return;
      const groups = q.data?.structured?.project?.eventGroups ?? [];
      for (const g of groups)
        for (const e of (g.events ?? []) as XpStructureEvent[]) {
          const start = e.startDate ? Date.parse(e.startDate) : NaN;
          const stations = e.stations?.length ?? 0;
          const reporters = e.reporters?.length ?? 0;
          out.push({
            id: entityIdOf(e.id),
            name: e.name ?? 'Event',
            group: g.name ?? '',
            projectId: proj.id,
            status: deriveEventStatus(e),
            start,
            stations,
            reporters,
            hasGaps: stations === 0 || reporters === 0,
          });
        }
    });
    return out;
  }, [structureQs, brandProjects]);

  const liveCount = events.filter((e) => e.status === 'live').length;
  const gapEvents = events.filter((e) => e.status !== 'completed' && e.hasGaps);
  const nudge = events.find((e) => e.status === 'live') ?? gapEvents[0] ?? null;

  const now = Date.now();
  const today = events.filter((e) => Number.isFinite(e.start) && Math.abs(e.start - now) < DAY);
  const thisWeek = events.filter(
    (e) => Number.isFinite(e.start) && e.start > now + DAY && e.start <= now + 7 * DAY,
  );

  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const firstName = user?.firstname || user?.email?.split('@')[0] || '';

  const loading = brandsQ.isLoading || projectsQ.isLoading;
  const error = brandsQ.isError || projectsQ.isError;

  return (
    <div className="page flex flex-col gap-6">
      {/* ATLEDI greeting — opens the day (G1, mirrors prototype AtlediHome) */}
      <AtlediGreeting
        greeting={greeting}
        firstName={firstName}
        nudge={nudge}
        liveCount={liveCount}
        gapCount={gapEvents.length}
        recents={brandProjects.slice(0, 3)}
      />

      {error && (
        <div className="rounded-xl border border-[var(--color-error)] bg-[var(--color-error)]/10 px-4 py-3 text-sm text-[var(--color-error)]">
          No se pudieron cargar marcas o proyectos. Reintenta más tarde.
        </div>
      )}
      {loading && (
        <div className="mono text-sm text-[var(--color-xk-text-muted)]">Cargando workspace…</div>
      )}

      {!loading && !error && brands.length === 0 && (
        <div className="rounded-2xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-6 py-12 text-center">
          <p className="text-sm text-[var(--color-xk-text-muted)]">
            Aún no tienes marcas accesibles. Crea o solicita acceso a una marca para empezar.
          </p>
        </div>
      )}

      {!loading && !error && brands.length > 0 && (
        <>
          {/* Workspace divider with live/gap summary */}
          <div className="flex items-center gap-4">
            <span className="mono flex-none text-[10px] uppercase tracking-wider text-[var(--color-xk-text-muted)]">
              Your workspace
            </span>
            <span className="h-px flex-1 bg-[var(--color-xk-border)]" />
            <span className="mono flex-none text-[11px] text-[var(--color-xk-text-subtle)]">
              {company?.name ? `${company.name} · ` : ''}
              {brands.length} brand{brands.length === 1 ? '' : 's'}
              {liveCount > 0 && (
                <>
                  {' · '}
                  <span className="font-bold text-[#4dd28b]">{liveCount} live</span>
                </>
              )}
              {gapEvents.length > 0 && (
                <>
                  {' · '}
                  <span className="text-[var(--color-error)]">
                    {gapEvents.length} gap{gapEvents.length === 1 ? '' : 's'}
                  </span>
                </>
              )}
            </span>
          </div>

          {fieldPhotos.length > 0 && <PhotoStrip photos={fieldPhotos} />}

          <BrandSwitcher
            brands={brands}
            activeBrandId={activeBrandId}
            onSwitch={setSelectedBrandId}
          />

          {activeBrand && (
            <BrandHero
              name={activeBrand.name}
              accent={activeBrand.secondaryColor || 'var(--color-xk-accent)'}
              projectCount={brandProjects.length}
              totalSamples={totalSamples}
              liveCount={liveCount}
            />
          )}

          {/* 2-col: pipeline + feeds (mirrors prototype) */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
            <div className="flex flex-col gap-4">
              <PtmPipeline projects={brandProjects} />
              <section className="flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <h2 className="mono text-xs font-bold uppercase tracking-wider text-[var(--color-xk-text-muted)]">
                    Projects
                  </h2>
                  <span className="mono text-xs text-[var(--color-xk-text-subtle)]">
                    {brandProjects.length}
                  </span>
                </div>
                {brandProjects.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-[var(--color-xk-border)] px-6 py-10 text-center text-sm text-[var(--color-xk-text-muted)]">
                    Esta marca aún no tiene proyectos.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {brandProjects.map((p) => (
                      <ProjectCard key={p.id} project={p} />
                    ))}
                  </div>
                )}
              </section>
            </div>

            {/* Feeds: Today / This week / Needs you */}
            <div className="flex flex-col gap-4">
              <HomeFeed title="Today" events={today} emptyHint="Sin eventos hoy." tone="#4dd28b" />
              <HomeFeed title="This week" events={thisWeek} emptyHint="Nada esta semana." tone="#5aa9ff" />
              <HomeFeed
                title="Needs you"
                events={gapEvents}
                emptyHint="Sin pendientes. 🎉"
                tone="var(--color-error)"
                showGap
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/** ATLEDI greeting block — orb + question + live nudge + action cards + recents. */
function AtlediGreeting({
  greeting,
  firstName,
  nudge,
  liveCount,
  gapCount,
  recents,
}: {
  greeting: string;
  firstName: string;
  nudge: HomeEvent | null;
  liveCount: number;
  gapCount: number;
  recents: XpProject[];
}) {
  const dateLabel = new Date().toLocaleDateString('en', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
  return (
    <div className="flex flex-col items-center gap-4 pt-2 text-center">
      <span
        className="h-16 w-16 rounded-full"
        style={{ background: 'radial-gradient(circle at 35% 30%, #d6ff5a, #b6e23a 70%)' }}
      />
      <div>
        <div className="mono text-[11px] font-bold uppercase tracking-[0.2em] text-[var(--color-xk-accent)]">
          ATLEDI · {dateLabel}
        </div>
        <h1 className="font-display mt-1 text-3xl font-extrabold tracking-tight text-[var(--color-xk-lavender)]">
          What's the word{firstName ? `, ${firstName}` : ''}?
        </h1>
        <p className="mx-auto mt-2 max-w-xl text-sm text-[var(--color-xk-text-muted)]">
          {greeting}. Plan the activation, track it live, then measure what it actually did —
          I'll keep the urgent thing in front of you.
        </p>
      </div>

      {nudge && (
        <Link
          to={`/projects/${nudge.projectId}`}
          className="flex max-w-2xl items-center gap-2 rounded-full border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-4 py-2 text-sm text-[var(--color-xk-lavender)] transition-colors hover:border-[var(--color-xk-accent)]"
        >
          {nudge.status === 'live' ? (
            <span className="h-2 w-2 flex-none animate-pulse rounded-full bg-[#4dd28b]" />
          ) : (
            <AlertTriangle className="h-3.5 w-3.5 flex-none text-[var(--color-error)]" />
          )}
          <span className="truncate">
            <span className="font-bold">{nudge.name}</span>{' '}
            {nudge.status === 'live' ? (
              <span className="text-[#4dd28b]">is live now</span>
            ) : (
              <span className="text-[var(--color-xk-text-muted)]">
                needs {nudge.stations === 0 ? 'stations' : 'reporters'}
              </span>
            )}
            {gapCount > 0 && (
              <span className="text-[var(--color-xk-text-muted)]">
                {' '}· {gapCount} event{gapCount === 1 ? '' : 's'} with open gaps
              </span>
            )}
          </span>
          <ArrowRight className="h-3.5 w-3.5 flex-none text-[var(--color-xk-text-muted)]" />
        </Link>
      )}

      <div className="grid w-full max-w-3xl grid-cols-1 gap-3 sm:grid-cols-3">
        <ActionCard
          to="/projects"
          icon={<FolderOpen className="h-5 w-5" />}
          title="New project"
          sub="Objective, audience, first event date — set up to measure"
        />
        <ActionCard
          to="/brands"
          icon={<Palette className="h-5 w-5" />}
          title="New brand"
          sub="Spin up a workspace for another brand you manage"
        />
        <ActionCard
          to={nudge ? `/projects/${nudge.projectId}` : '/projects'}
          icon={<Activity className="h-5 w-5" />}
          title="Track live events"
          sub={liveCount > 0 ? `${liveCount} live right now` : "Jump into what's happening in-market"}
        />
      </div>

      {recents.length > 0 && (
        <div className="flex flex-wrap items-center justify-center gap-2">
          <span className="mono text-[10px] uppercase tracking-wider text-[var(--color-xk-text-subtle)]">
            Recent
          </span>
          {recents.map((p) => (
            <Link
              key={p.id}
              to={`/projects/${p.id}`}
              className="flex items-center gap-1.5 rounded-full border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-3 py-1 text-xs text-[var(--color-xk-lavender)] hover:border-[var(--color-xk-accent)]"
            >
              <Sparkles className="h-3 w-3 text-[var(--color-xk-accent)]" />
              <span className="max-w-[180px] truncate">{p.name}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function ActionCard({
  to,
  icon,
  title,
  sub,
}: {
  to: string;
  icon: React.ReactNode;
  title: string;
  sub: string;
}) {
  return (
    <Link
      to={to}
      className="group flex flex-col gap-3 rounded-2xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] p-4 text-left transition-colors hover:border-[var(--color-xk-accent)]"
    >
      <span className="grid h-10 w-10 place-items-center rounded-xl bg-[var(--color-accent-10)] text-[var(--color-xk-accent)]">
        {icon}
      </span>
      <div>
        <div className="text-sm font-bold text-[var(--color-xk-lavender)]">{title}</div>
        <div className="mt-0.5 text-xs text-[var(--color-xk-text-muted)]">{sub}</div>
      </div>
    </Link>
  );
}

/** Today / This week / Needs-you feed column. */
function HomeFeed({
  title,
  events,
  emptyHint,
  tone,
  showGap,
}: {
  title: string;
  events: HomeEvent[];
  emptyHint: string;
  tone: string;
  showGap?: boolean;
}) {
  return (
    <section className="flex flex-col gap-2">
      <h2 className="mono text-xs font-bold uppercase tracking-wider text-[var(--color-xk-text-muted)]">
        {title}
        <span className="ml-2 text-[var(--color-xk-text-subtle)]">{events.length}</span>
      </h2>
      {events.length === 0 ? (
        <div className="mono rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-3 py-2.5 text-[11px] text-[var(--color-xk-text-subtle)]">
          {emptyHint}
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          {events.slice(0, 6).map((e) => (
            <Link
              key={e.id}
              to={`/projects/${e.projectId}`}
              className="flex items-center gap-2.5 rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-3 py-2 transition-colors hover:border-[var(--color-xk-accent)]"
            >
              <span className="h-8 w-1 flex-none rounded-full" style={{ background: tone }} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-xs font-semibold text-[var(--color-xk-lavender)]">
                  {e.name}
                </div>
                <div className="mono truncate text-[10px] text-[var(--color-xk-text-muted)]">
                  {e.group}
                  {Number.isFinite(e.start) && (
                    <>
                      {' · '}
                      {new Date(e.start).toLocaleDateString('es', {
                        day: 'numeric',
                        month: 'short',
                        timeZone: 'UTC',
                      })}
                    </>
                  )}
                </div>
              </div>
              {showGap && (
                <span className="mono flex-none text-[9px] text-[var(--color-error)]">
                  {e.stations === 0 ? 'sin stations' : 'sin reporters'}
                </span>
              )}
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

const PURPOSE_COLOR: Record<string, string> = {
  SEE: '#5aa9ff', ENGAGE: '#f4b740', TRY: '#4dd28b', ACT: '#ff5a4f',
};

/** "From the field" — horizontal strip of real activation photos (F9). */
function PhotoStrip({ photos }: { photos: XpEventAsset[] }) {
  return (
    <section className="flex flex-col gap-2">
      <h2 className="mono text-xs font-bold uppercase tracking-wider text-[var(--color-xk-text-muted)]">
        From the field
      </h2>
      <div className="scrollbar-none flex gap-3 overflow-x-auto pb-2">
        {photos.map((p) => {
          const purpose = p.assetObjetive ? PURPOSE_BY_ID[p.assetObjetive] : null;
          return (
            <Link
              key={p.id}
              to="/photos"
              className="relative h-28 w-28 flex-none overflow-hidden rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-bg)]"
            >
              <img
                src={p.image!}
                alt={p.description ?? 'field photo'}
                loading="lazy"
                className="h-full w-full object-cover"
              />
              {purpose && (
                <span
                  className="mono absolute left-1 top-1 rounded-full px-1.5 py-0.5 text-[8px] font-bold text-white"
                  style={{ background: PURPOSE_COLOR[purpose] }}
                >
                  {purpose}
                </span>
              )}
            </Link>
          );
        })}
      </div>
    </section>
  );
}

const PTM_COLS = [
  { id: 'plan', label: 'Plan', color: 'var(--color-xk-accent)', statuses: ['plan'] },
  { id: 'track', label: 'Track', color: '#4dd28b', statuses: ['live'] },
  { id: 'measure', label: 'Measure', color: '#5aa9ff', statuses: ['completed'] },
] as const;

/** PTM pipeline (build-v2 F2): events across the brand's projects flow
 * Plan → Track → Measure by derived status. Reads each project's structure. */
function PtmPipeline({ projects }: { projects: XpProject[] }) {
  const structureQs = useQueries({
    queries: projects.map((p) => ({
      queryKey: queryKeys.xamplin.projectStructure(p.id),
      queryFn: () => xamplinApi.projects.structure(p.id),
    })),
  });

  const events = structureQs.flatMap((q, i) => {
    const proj = projects[i];
    const groups = q.data?.structured?.project?.eventGroups ?? [];
    return groups.flatMap((g) =>
      (g.events ?? []).map((e) => ({
        id: e.id,
        name: e.name ?? 'Event',
        group: g.name ?? '',
        projectId: proj.id,
        status: deriveEventStatus(e),
        stations: e.stations?.length ?? 0,
      })),
    );
  });
  const loading = structureQs.some((q) => q.isLoading);

  return (
    <section className="flex flex-col gap-3">
      <h2 className="mono text-xs font-bold uppercase tracking-wider text-[var(--color-xk-text-muted)]">
        Activation pipeline
      </h2>
      {loading ? (
        <div className="mono text-sm text-[var(--color-xk-text-muted)]">Cargando pipeline…</div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {PTM_COLS.map((col) => {
            const colEvents = events.filter((e) =>
              (col.statuses as readonly string[]).includes(e.status),
            );
            return (
              <div
                key={col.id}
                className="flex flex-col rounded-2xl border"
                style={{ borderColor: col.color + '40', background: col.color + '0a' }}
              >
                <div
                  className="flex items-center gap-2 border-b px-3 py-2"
                  style={{ borderColor: col.color + '22' }}
                >
                  <span
                    className="mono text-[11px] font-bold uppercase tracking-wider"
                    style={{ color: col.color }}
                  >
                    {col.label}
                  </span>
                  <span className="mono ml-auto text-[11px]" style={{ color: col.color }}>
                    {colEvents.length}
                  </span>
                </div>
                <div className="flex min-h-[60px] flex-col gap-1.5 p-2">
                  {colEvents.length === 0 ? (
                    <div className="mono px-1 py-3 text-center text-[10px] text-[var(--color-xk-text-subtle)]">
                      — sin eventos —
                    </div>
                  ) : (
                    colEvents.slice(0, 8).map((e) => (
                      <Link
                        key={e.id}
                        to={`/projects/${e.projectId}`}
                        className="rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-3 py-2 transition-colors hover:border-[var(--color-xk-accent)]"
                      >
                        <div className="truncate text-xs font-semibold text-[var(--color-xk-lavender)]">
                          {e.name}
                        </div>
                        <div className="mono mt-0.5 flex items-center gap-2 text-[10px] text-[var(--color-xk-text-muted)]">
                          {e.group && <span className="truncate">{e.group}</span>}
                          <span className="ml-auto">{e.stations} st</span>
                        </div>
                      </Link>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

/** Chips when few brands; a compact styled <select> once the list grows. */
function BrandSwitcher({
  brands,
  activeBrandId,
  onSwitch,
}: {
  brands: { id: number; name: string; secondaryColor?: string | null }[];
  activeBrandId: number | null;
  onSwitch: (id: number) => void;
}) {
  const active = brands.find((b) => b.id === activeBrandId) ?? null;

  if (brands.length > 8) {
    return (
      <div className="flex items-center gap-2">
        <span className="mono text-[10px] uppercase tracking-wider text-[var(--color-xk-text-subtle)]">
          Brand
        </span>
        <span
          className="grid h-6 w-6 place-items-center rounded-full text-[10px] font-extrabold text-white"
          style={{ background: active?.secondaryColor || 'var(--color-xk-accent)' }}
        >
          {(active?.name ?? '?').slice(0, 1).toUpperCase()}
        </span>
        <select
          value={activeBrandId ?? ''}
          onChange={(e) => onSwitch(Number(e.target.value))}
          className="rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-3 py-1.5 text-sm text-[var(--color-xk-lavender)] outline-none focus-visible:border-[var(--color-xk-accent)]"
          aria-label="Select brand"
        >
          {brands.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
        <span className="mono text-[10px] text-[var(--color-xk-text-subtle)]">
          {brands.length} brands
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="mono mr-1 text-[10px] uppercase tracking-wider text-[var(--color-xk-text-subtle)]">
        Brand
      </span>
      {brands.map((b) => {
        const isActive = b.id === activeBrandId;
        return (
          <button
            key={b.id}
            type="button"
            onClick={() => onSwitch(b.id)}
            className={cn(
              'flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors',
              isActive
                ? 'border-[var(--color-xk-accent)] bg-[var(--color-accent-10)] text-[var(--color-xk-lavender)]'
                : 'border-[var(--color-xk-border)] text-[var(--color-xk-text-muted)] hover:bg-[var(--color-xk-surface)]',
            )}
          >
            <span
              className="grid h-5 w-5 place-items-center rounded-full text-[9px] font-extrabold text-white"
              style={{ background: b.secondaryColor || 'var(--color-xk-accent)' }}
            >
              {b.name.slice(0, 1).toUpperCase()}
            </span>
            {b.name}
          </button>
        );
      })}
    </div>
  );
}

function BrandHero({
  name,
  accent,
  projectCount,
  totalSamples,
  liveCount,
}: {
  name: string;
  accent: string;
  projectCount: number;
  totalSamples: number;
  liveCount: number;
}) {
  return (
    <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-5 py-4">
      <span
        className="grid h-12 w-12 flex-none place-items-center rounded-xl text-base font-extrabold text-white"
        style={{ background: accent }}
      >
        {name.slice(0, 1).toUpperCase()}
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-base font-bold text-[var(--color-xk-lavender)]">{name}</div>
        <div className="mono text-xs text-[var(--color-xk-text-muted)]">
          {projectCount} project{projectCount === 1 ? '' : 's'}
        </div>
      </div>
      <Kpi label="Projects" value={<CountUp value={projectCount} />} />
      <Kpi label="Samples goal" value={<CountUp value={totalSamples} />} />
      <Kpi
        label="Live now"
        value={liveCount > 0 ? String(liveCount) : '—'}
        tone={liveCount > 0 ? '#4dd28b' : undefined}
      />
    </div>
  );
}

function Kpi({ label, value, hint, tone }: { label: string; value: React.ReactNode; hint?: string; tone?: string }) {
  return (
    <div className="text-right">
      <div
        className="font-display text-xl font-extrabold"
        style={{ color: tone ?? 'var(--color-xk-lavender)' }}
      >
        {value}
      </div>
      <div className="mono text-[10px] uppercase tracking-wider text-[var(--color-xk-text-subtle)]">
        {label}
        {hint && <span className="ml-1 text-[var(--color-xk-accent)]">{hint}</span>}
      </div>
    </div>
  );
}

function ProjectCard({ project }: { project: XpProject }) {
  const dates =
    project.startDate && project.endDate
      ? `${project.startDate.slice(0, 10)} → ${project.endDate.slice(0, 10)}`
      : 'Sin fechas';
  return (
    <Link
      to={`/projects/${project.id}`}
      className="group flex flex-col gap-2 rounded-2xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] p-4 transition-colors hover:border-[var(--color-xk-accent)]"
    >
      <div className="flex items-start gap-2">
        <FolderOpen className="mt-0.5 h-4 w-4 flex-none text-[var(--color-xk-accent)]" />
        <span className="line-clamp-2 flex-1 text-sm font-semibold text-[var(--color-xk-lavender)]">
          {project.name}
        </span>
      </div>
      <div className="mono text-[11px] text-[var(--color-xk-text-muted)]">{dates}</div>
      <div className="flex items-center gap-2">
        {project.marketingObjectivePrimary && (
          <span className="mono rounded-full border border-[var(--color-xk-border)] px-2 py-0.5 text-[10px] text-[var(--color-xk-text-muted)]">
            {project.marketingObjectivePrimary}
          </span>
        )}
        <span className="mono ml-auto text-[10px] text-[var(--color-xk-text-subtle)]">
          {(project.samples ?? 0).toLocaleString()} samples
        </span>
      </div>
    </Link>
  );
}

/** First-run hint to create a brand/project — reserved for empty workspaces. */
export function HomeEmptyCta() {
  return (
    <button
      type="button"
      className="flex items-center gap-2 rounded-full border border-dashed border-[var(--color-xk-border)] px-3 py-1.5 text-xs text-[var(--color-xk-text-muted)]"
    >
      <Plus className="h-3.5 w-3.5" /> Brand
    </button>
  );
}
